import { ActivityFeedEntryDto, EntityTypeEnum } from 'api/activity-feed';

/**
 * Extracts a person's name from an activity feed message.
 * Handles multiple formats:
 * - "{Personne:xxx} (Nom Prenom)" -> "Nom Prenom"
 * - "Nom Prenom a fait..." -> "Nom Prenom"
 * - "Nom Prenom - action..." -> "Nom Prenom"
 * - Falls back to title words if no name found
 */
export const extractNameFromMessage = (
  message: string,
  title?: string
): string => {
  // Pattern 1: {Personne:xxx} (Nom Prenom) - extract from parentheses after Personne reference
  const personnePattern = /\{Personne:[^}]+\}\s*\(([^)]+)\)/;
  const personneMatch = message.match(personnePattern);
  if (personneMatch) {
    return personneMatch[1].trim();
  }

  // Pattern 2: Name at the beginning followed by " a " (French action pattern)
  // e.g., "Jean Dupont a signé le contrat"
  const actionPattern = /^([A-ZÀ-ÿ][a-zà-ÿ]+(?:\s+[A-ZÀ-ÿ][a-zà-ÿ]+)+)\s+a\s+/;
  const actionMatch = message.match(actionPattern);
  if (actionMatch) {
    return actionMatch[1].trim();
  }

  // Pattern 3: Name followed by " - " (dash separator pattern)
  // e.g., "Marie Dupont - Échéance de paiement..."
  const dashPattern = /^([A-ZÀ-ÿ][a-zà-ÿ]+(?:\s+[A-ZÀ-ÿ][a-zà-ÿ]+)+)\s+-\s+/;
  const dashMatch = message.match(dashPattern);
  if (dashMatch) {
    return dashMatch[1].trim();
  }

  // Pattern 4: Any name pattern with first and last name capitalized
  // e.g., "Matahi Maconfiancia" anywhere in message
  const namePattern = /([A-ZÀ-ÿ][a-zà-ÿ]+\s+[A-ZÀ-ÿ][a-zà-ÿ]+)/;
  const nameMatch = message.match(namePattern);
  if (nameMatch) {
    return nameMatch[1].trim();
  }

  // Fallback: use first two words of title if available
  if (title) {
    const titleWords = title.split(' ').slice(0, 2).join(' ');
    return titleWords;
  }

  return '';
};

/**
 * Generates initials from a name or title.
 * - "Jean Dupont" -> "JD"
 * - "Marie-Claire Bernard" -> "MB"
 * - "Contrat signé" -> "CS"
 */
export const getInitials = (name: string): string => {
  if (!name) return '??';

  // Split by spaces and hyphens, take first letter of each significant word
  const words = name.split(/[\s-]+/).filter((w) => w.length > 0);

  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  if (words.length === 1 && words[0].length >= 2) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return '??';
};

/**
 * Gets initials for an activity entry, with smart fallbacks
 */
export const getActivityInitials = (
  message: string,
  title: string
): string => {
  const name = extractNameFromMessage(message, title);
  return getInitials(name || title);
};

// Helper to format relative time
export const formatRelativeTime = (date: Date | string): string => {
  // Ensure we have a valid Date object
  const dateObj = date instanceof Date ? date : new Date(date);

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return "Date inconnue";
  }

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();

  // Handle future dates (negative diff) - treat as "à l'instant"
  if (diffMs < 0) {
    return "à l'instant";
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) {
    return "à l'instant";
  }
  if (diffMinutes < 60) {
    return `il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  }
  return dateObj.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Get badge info based on entry title
export const getStatusBadge = (
  entry: ActivityFeedEntryDto
): { label: string; bgColor: string; textColor: string } | null => {
  const titleLower = entry.title.toLowerCase();
  const messageLower = entry.message.toLowerCase();

  if (titleLower.includes('signe') || messageLower.includes('signe')) {
    return {
      label: 'A signer',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700'
    };
  }

  if (titleLower.includes('valider') || titleLower.includes('validation')) {
    return {
      label: 'A valider',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700'
    };
  }

  if (titleLower.includes('echeance') || titleLower.includes('impaye')) {
    return {
      label: 'Echeance proche',
      bgColor: 'bg-red-100',
      textColor: 'text-red-700'
    };
  }

  if (titleLower.includes('document')) {
    return {
      label: 'Documents',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700'
    };
  }

  if (titleLower.includes('paiement')) {
    return {
      label: 'Paiement',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700'
    };
  }

  return {
    label: 'A traiter',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700'
  };
};

// Get avatar color based on initials
export const getAvatarColor = (initials: string): string => {
  const colors = [
    'bg-orange-500',
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-teal-500'
  ];
  const index = initials.charCodeAt(0) % colors.length;
  return colors[index];
};
